/**
 * The colours behind every percentile / rank chip in the Explorer and Player Stats grids: the team report's scale
 * (`util/reportColors`, the staff's 2026-10-10 pick), red = bad, green = good, in seven buckets with a white middle.
 * Before 2026-11-02 the grids had their own continuous blue -> red ramp (and indigo -> teal for ranks), which read
 * as a different language from the reports the same people open beside them.
 *
 * Ranks bucket on the rank itself, out of its population, as the report does: converting a rank to a percentile
 * first puts rank 5 of 30 in a different bucket depending on which conversion the caller chose.
 *
 * A neutral column (a frequency or a volume: how often, not how well) has no good end, so its chip is white with
 * the middle bucket's grey ring and grey numerals: the number is there, the judgement isn't.
 */
import {
  Step,
  WHITE_CELL_OUTLINE,
  bucketSpans,
  stepColor,
  stepFromPercentile,
  stepFromRank,
  stepHexOverWhite,
} from "../../../util/reportColors";

export type PercentileMode = "percentiles" | "ranks";

export interface PercentileColor {
  /** Chip background (translucent, like the report's chips, so zebra rows show through the white middle). */
  fill: string;
  /** Text colour. */
  text: string;
  /** `text-shadow` outlining `text` so it stays legible on the full-strength ends. */
  textOutline: string;
  /** `box-shadow` ring: the white middle bucket and neutral chips get one so they don't vanish; others "none". */
  outline: string;
}

export interface PercentileColorOptions {
  /** 1 = best. When given with `population`, the bucket comes from the rank, not the percentile. */
  rank?: number;
  population?: number;
  /** A column with no good end: white chip, grey ring, grey numerals. */
  neutral?: boolean;
}

/**
 * Text stays one colour at every bucket, kept legible on the full-strength ends by an outline rather than by
 * flipping to white (a flip reads as a threshold in the data).
 *
 * Four 1px offsets at **zero blur**, matching the report's chip: a blurred halo bleeds into the glyph and makes
 * 11px bold numerals look soft. Expressed as `text-shadow`, not `filter: drop-shadow`: a filter promotes every chip
 * to its own compositor layer, which desynchronises from the grid's sticky pinned columns on a horizontal scroll.
 */
const TEXT = "rgba(0, 0, 0, 0.87)";
const NEUTRAL_TEXT = "rgba(0, 0, 0, 0.55)";
const OUTLINE_COLOR = "rgba(255, 255, 255, 0.6)";
const TEXT_OUTLINE = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
]
  .map(([x, y]) => `${x}px ${y}px 0 ${OUTLINE_COLOR}`)
  .join(", ");

/** The bucket a chip falls in: by rank when it has one, else by percentile (100 = best). */
export function chipStep(
  percentile: number | null | undefined,
  { rank, population }: PercentileColorOptions = {},
): Step {
  if (rank != null && population != null && population > 0) {
    return stepFromRank(rank, population);
  }
  if (percentile == null || Number.isNaN(percentile)) return 0;
  return stepFromPercentile(Math.max(0, Math.min(100, percentile)));
}

/**
 * @param percentile 0-100, 100 = best. Null / NaN reads as the white middle, so a missing value never renders as
 *   "elite" or "awful".
 */
export function percentileColor(
  percentile: number | null | undefined,
  options: PercentileColorOptions = {},
): PercentileColor {
  if (options.neutral) {
    return {
      fill: "#fff",
      text: NEUTRAL_TEXT,
      textOutline: "none",
      outline: WHITE_CELL_OUTLINE,
    };
  }
  const step = chipStep(percentile, options);
  return {
    fill: stepColor(step),
    text: TEXT,
    textOutline: TEXT_OUTLINE,
    outline: step === 0 ? WHITE_CELL_OUTLINE : "none",
  };
}

/**
 * The legend swatch: the seven buckets as hard-stop blocks, sampled from the same scale as the cells.
 *
 * `direction` is the CSS gradient direction: stops run worst -> best, so `to right` reads bad-left, good-right
 * (percentile legends: 0 ... 100) and `to left` puts the best end on the left (rank legends: 1st ... Last).
 */
export function percentileGradient(
  direction: "to right" | "to left" = "to right",
): string {
  const stops = bucketSpans().flatMap(({ from, to, step }) => {
    const color = stepHexOverWhite(step);
    return [
      `${color} ${((from / 30) * 100).toFixed(2)}%`,
      `${color} ${((to / 30) * 100).toFixed(2)}%`,
    ];
  });
  return `linear-gradient(${direction}, ${stops.join(", ")})`;
}

/** "1st", "2nd", "23rd" — used by rank chips. */
export function ordinalSuffix(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = n % 100;
  return suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
}
